import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ShopProductAssignBody } from "@/modules/shops/domain/contracts";
import type { ShopFeatureFlags } from "@/modules/shops/domain/contracts/shop-feature-flags";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type {
  CreateBranchInput,
  CreateShopInput,
  CreateStaffInput,
  UpdateBranchInput,
  UpdateShopInput,
  UpdateStaffInput,
} from "./client";
import { shopsApi } from "./client";

export const shopsKeys = {
  all: ["shops"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["shops", "list", q] as const,
  stats: ["shops", "stats"] as const,
  detail: (id: string) => ["shops", "detail", id] as const,
  branches: (shopId: string) => ["shops", "branches", shopId] as const,
  products: (shopId: string, q: Record<string, unknown>) =>
    ["shops", "products", shopId, q] as const,
  performance: (period: string) => ["shops", "performance", period] as const,
};

export function useCreateShop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShopInput) => shopsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useShopQuery(id: string) {
  return useQuery({
    queryKey: shopsKeys.detail(id),
    queryFn: () => shopsApi.getById(id),
    enabled: !!id,
  });
}

export function useAddShopStaff(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStaffInput) => shopsApi.addStaff(shopId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useUpdateShopStaff(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffId,
      input,
    }: {
      staffId: string;
      input: UpdateStaffInput;
    }) => shopsApi.updateStaff(shopId, staffId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useUpdateShop(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateShopInput) => shopsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useUpdateShopFeatureFlags(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ShopFeatureFlags>) =>
      shopsApi.patchFeatureFlags(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useSuspendShop(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => shopsApi.suspend(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useUnsuspendShop(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => shopsApi.unsuspend(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useVerifyShop(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (level: "basic" | "verified") => shopsApi.verify(id, level),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useShopsStatsQuery() {
  return useQuery({
    queryKey: shopsKeys.stats,
    queryFn: () => shopsApi.stats(),
  });
}

export function useShopsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: shopsKeys.list(q),
    queryFn: () => shopsApi.list(q),
  });
}

export function useBranchesQuery(shopId: string) {
  return useQuery({
    queryKey: shopsKeys.branches(shopId),
    queryFn: () => shopsApi.getBranches(shopId),
    enabled: !!shopId,
  });
}

export function useShopPerformanceQuery(
  period: "day" | "week" | "month" = "week",
) {
  return useQuery({
    queryKey: shopsKeys.performance(period),
    queryFn: () => shopsApi.getPerformance(period),
  });
}

export function useCreateBranchMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBranchInput) =>
      shopsApi.createBranch(shopId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.branches(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.detail(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useUpdateBranchMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      branchId,
      input,
    }: {
      branchId: string;
      input: UpdateBranchInput;
    }) => shopsApi.updateBranch(shopId, branchId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.branches(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.detail(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useSetBranchActiveMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      branchId,
      isActive,
    }: {
      branchId: string;
      isActive: boolean;
    }) => shopsApi.setBranchActive(shopId, branchId, isActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.branches(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.detail(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

export function useDeleteBranchMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (branchId: string) => shopsApi.deleteBranch(shopId, branchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopsKeys.branches(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.detail(shopId) });
      qc.invalidateQueries({ queryKey: shopsKeys.all });
    },
  });
}

// ----- C2. Shop Products -----
export function useShopProductsQuery(
  shopId: string,
  params: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string;
  } = {},
) {
  const queryKey = shopsKeys.products(shopId, params);
  return useQuery({
    queryKey,
    queryFn: () => shopsApi.listShopProducts(shopId, params),
    enabled: !!shopId,
  });
}

export function useAssignProductMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShopProductAssignBody) =>
      shopsApi.assignProduct(shopId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shops", "products", shopId] });
    },
  });
}

export function useUpdateShopProductSkuMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopProductSkuId,
      body,
    }: {
      shopProductSkuId: string;
      body: {
        sku?: string;
        barcode?: string | null;
        price?: number;
        cost?: number | null;
        currency?: "LAK" | "THB" | "USD";
        isActive?: boolean;
      };
    }) => shopsApi.updateShopProductSku(shopId, shopProductSkuId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shops", "products", shopId] });
    },
  });
}

export function useRemoveShopProductSkuMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopProductSkuId,
      mode = "hard",
    }: {
      shopProductSkuId: string;
      mode?: "soft" | "hard";
    }) => shopsApi.removeShopProductSku(shopId, shopProductSkuId, mode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shops", "products", shopId] });
    },
  });
}

export function useUnassignShopProductMutation(shopId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shopProductId: string) =>
      shopsApi.unassignShopProduct(shopId, shopProductId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shops", "products", shopId] });
    },
  });
}
